/**
 * Service for BeeCare order calculations
 */
import references from "./references";

/**
 * Extract a numeric price from either a plain number/string or a
 * Shopify MoneyV2-style object ({ amount }).
 */
const extractPrice = (price) => {
  if (price && typeof price === "object" && price.amount != null) {
    return Number(price.amount);
  }
  return Number(price);
};

/**
 * Derive the BeeCare bulk-pricing tiers directly from the Shopify variants
 * of the "beehome-beecare" product, instead of a hardcoded local table.
 *
 * Each paid tier variant's title ends in the quantity it applies to, e.g.
 * "Wildbienen-Pflege 1" / "Wildbienen-Pflege 2" / "Wildbienen-Pflege 3"
 * (the SKU is identical across variants, so the title is what identifies
 * the tier). The free voucher variant ("kostenlose Wildbienen-Pflege",
 * price 0) has no trailing number and is excluded. Adding, removing, or
 * repricing a tier is done entirely in Shopify - no code change needed.
 */
const getBulkPricingTiers = (allShopifyVariants = []) => {
  return allShopifyVariants
    .filter((variant) => variant.available !== false)
    .reduce((tiers, variant) => {
      const price = extractPrice(variant.price);
      if (!price) return tiers; // skip the free/voucher variant

      const match = String(variant.title || "").match(/(\d+)\s*$/);
      if (!match) return tiers; // skip variants that don't encode a tier quantity

      tiers.push({ minimum: Number(match[1]), price, variant });
      return tiers;
    }, [])
    .sort((a, b) => a.minimum - b.minimum);
};

/**
 * Calculate bulk pricing based on quantity of houses
 * Returns both the discounted price and original price for comparison
 */
export const calculateBulkPricing = (
  numberOfHouses,
  allShopifyVariants = []
) => {
  const tiers = getBulkPricingTiers(allShopifyVariants);

  if (tiers.length === 0) {
    // Shopify variants not loaded yet - fall back to the local default
    const fallbackPrice = references.defaultOrders.beecare.price;
    return {
      originalPrice: Number(fallbackPrice),
      currentPrice: Number(fallbackPrice),
      priceReduction: 0,
      variant: null,
    };
  }

  // The highest tier whose minimum is at or below the selected quantity
  // applies (so the top tier also covers everything above it, e.g. "3+").
  const currentTier = tiers.reduce(
    (selected, tier) => (tier.minimum <= numberOfHouses ? tier : selected),
    tiers[0]
  );

  const originalPrice = tiers[0].price; // single-house price is the lowest tier
  const currentPrice = currentTier.price;
  const priceReduction = (originalPrice - currentPrice) * numberOfHouses;

  return {
    originalPrice: Number(originalPrice),
    currentPrice: Number(currentPrice),
    priceReduction,
    variant: currentTier.variant,
  };
};

export const calculateBaseTotal = (amount, price) => {
  return amount * price;
};

export const calculateDiscountTotal = (vouchers, price) => {
  return vouchers.length * price;
};

export const calculateAdditionalOptionsTotal = (additionalOptions) => {
  let total = 0;

  if (!additionalOptions || !Array.isArray(additionalOptions)) return total;

  additionalOptions.forEach((item) => {
    // Check for the new addOnDetails array which contains Shopify product objects
    if (item.addOnDetails && Array.isArray(item.addOnDetails)) {
      item.addOnDetails.forEach((addOn) => {
        total += Number(addOn.price || 0);
      });
    }
  });

  return total;
};

export const calculateAdditionalProductsTotal = (additionalProducts = []) => {
  return additionalProducts.reduce(
    (total, product) =>
      total + Number(product.price || 0) * (product.quantity || 0),
    0
  );
};

/**
 * Centralized function to calculate complete order totals
 * This function handles ALL pricing calculations including bulk discounts
 */
export const calculateCompleteOrderTotal = ({
  selectedHouses = [],
  allShopifyVariants = [],
  discount_vouchers = [],
  donation = 0,
  additionalOptions = [],
  additionalProducts = [],
}) => {
  const numberOfHouses = selectedHouses.length;

  if (numberOfHouses === 0) {
    return {
      baseTotal: 0,
      discountTotal: 0,
      additionalOptionsTotal: 0,
      additionalProductsTotal: 0,
      donationTotal: Number(donation),
      finalTotal: Number(donation),
      originalPrice: references.defaultOrders.beecare.price,
      currentPrice: references.defaultOrders.beecare.price,
      priceReduction: 0,
      variant: null,
    };
  }

  // Calculate bulk pricing
  const pricingDetails = calculateBulkPricing(
    numberOfHouses,
    allShopifyVariants
  );

  // Calculate component totals
  const baseTotal = calculateBaseTotal(
    numberOfHouses,
    pricingDetails.currentPrice
  );
  const discountTotal = calculateDiscountTotal(
    discount_vouchers,
    pricingDetails.currentPrice
  );
  const additionalOptionsTotal =
    calculateAdditionalOptionsTotal(additionalOptions);
  const additionalProductsTotal =
    calculateAdditionalProductsTotal(additionalProducts);
  const donationTotal = Number(donation);

  // Calculate final total
  const finalTotal =
    baseTotal -
    discountTotal +
    donationTotal +
    additionalOptionsTotal +
    additionalProductsTotal;

  return {
    baseTotal,
    discountTotal,
    additionalOptionsTotal,
    additionalProductsTotal,
    donationTotal,
    finalTotal: Number(finalTotal.toFixed(2)),
    originalPrice: pricingDetails.originalPrice,
    currentPrice: pricingDetails.currentPrice,
    priceReduction: pricingDetails.priceReduction,
    variant: pricingDetails.variant,
  };
};

/**
 * Legacy function - kept for backward compatibility
 * Use calculateCompleteOrderTotal instead for new implementations
 */
export const calculateOrderTotal = ({
  amount,
  price,
  discount_vouchers = [],
  donation = 0,
  additionalOptions,
  additionalProducts = [],
}) => {
  const baseTotal = calculateBaseTotal(amount, price);
  const discountTotal = calculateDiscountTotal(discount_vouchers, price);
  const additionalOptionsTotal =
    calculateAdditionalOptionsTotal(additionalOptions);
  const additionalProductsTotal =
    calculateAdditionalProductsTotal(additionalProducts);

  return (
    baseTotal -
    discountTotal +
    Number(donation) +
    additionalOptionsTotal +
    additionalProductsTotal
  ).toFixed(2);
};

export const getAdditionalOptionsCounts = (additionalOptions) => {
  // This function is no longer used by the BeeCareOrderSideBar
  // as it directly uses the addOnDetails from the context.
  // However, it's kept here for potential other uses or backward compatibility.
  // It will not accurately reflect the new dynamic add-on prices.
  const counts = {
    birdify: { count: 0, total: 0, price: 40 },
    diversify: { count: 0, total: 0, price: 30 },
    refurbish: { count: 0, total: 0, price: 20 },
  };

  if (!additionalOptions) return counts;

  Object.values(additionalOptions).forEach((item) => {
    if (item.birdify) {
      counts.birdify.count += 1;
      counts.birdify.total += counts.birdify.price;
    }
    if (item.diversify) {
      counts.diversify.count += 1;
      counts.diversify.total += counts.diversify.price;
    }
    if (item.refurbish) {
      counts.refurbish.count += 1;
      counts.refurbish.total += counts.refurbish.price;
    }
  });

  return counts;
};
